/**
 * Suitsupply size chart drawer UI (matches suitsupply.com)
 * Fit data loaded from ss-size-chart-data.js
 */
class SsSizeChart {
  constructor(root) {
    this.root = root;
    this.fit = 'Regular';
    this.unit = 'cm';
    this.chartType = root.dataset.chartType || 'jacket';
    this.jacketFit = root.dataset.jacketFit || 'milano';
    this.trouserFit = root.dataset.trouserFit || 'duca';
    this.jacketTitle = root.dataset.jacketTitle || '';
    this.trouserTitle = root.dataset.trouserTitle || '';
    this.showJacket = root.dataset.showJacket !== 'false';
    this.showTrousers = root.dataset.showTrousers !== 'false';
    this.showConversions = root.dataset.showConversions !== 'false';
    this.showLengthTabs = root.dataset.showLengthTabs !== 'false';

    this.render();
    this.bind();
  }

  fitProfile(type) {
    const key = type === 'trouser' ? this.trouserFit : this.jacketFit;
    const group = type === 'trouser' ? SS_SIZE_CHART_FITS.trousers : SS_SIZE_CHART_FITS.jackets;
    return group[key] || group[Object.keys(group)[0]];
  }

  lengthRange(fit) {
    const type = this.chartType === 'trouser-standalone' ? 'trouser' : 'jacket';
    const profile = this.fitProfile(type);
    return profile.lengthRanges[fit] || '';
  }

  chartSizes(type) {
    if (type === 'shirt') {
      return SS_SIZE_CHART_META.shirtSizes.map((num) => ({ num, alpha: '' }));
    }
    if (type === 'knitwear') {
      return SS_SIZE_CHART_META.knitwearSizes;
    }
    const profile = this.fitProfile(type);
    const block = profile[this.fit];
    return block?.sizes || SS_SIZE_CHART_FITS.jackets.milano.Regular.sizes;
  }

  chartData(type) {
    if (type === 'shirt') return SS_SIZE_CHART_META.shirt.Regular;
    if (type === 'knitwear') return SS_SIZE_CHART_META.knitwear.Regular;
    const profile = this.fitProfile(type);
    const block = profile[this.fit];
    return block || profile.Regular;
  }

  sectionTitle(type) {
    if (type === 'jacket') {
      return this.jacketTitle || this.fitProfile('jacket').title;
    }
    return this.trouserTitle || this.fitProfile('trouser').title;
  }

  render() {
    const isSimple = this.chartType === 'shirt' || this.chartType === 'knitwear';
    const lengthTabs = this.showLengthTabs && !isSimple
      ? `
      <div class="ss-chart__length-tabs">
        <button type="button" class="ss-chart__length-tab" data-fit="Regular">
          <span class="ss-chart__length-label">Regular</span>
          <span class="ss-chart__length-range">${this.lengthRange('Regular')}</span>
        </button>
        <button type="button" class="ss-chart__length-tab" data-fit="Long">
          <span class="ss-chart__length-label">Long</span>
          <span class="ss-chart__length-range">${this.lengthRange('Long')}</span>
        </button>
        <button type="button" class="ss-chart__length-tab" data-fit="Short">
          <span class="ss-chart__length-label">Short</span>
          <span class="ss-chart__length-range">${this.lengthRange('Short')}</span>
        </button>
      </div>`
      : '';

    let body = '';
    if (this.chartType === 'shirt') {
      body = this.sectionHtml('shirt', 'Shirt', 'Shirt measurements', this.chartData('shirt'), 'shirt');
    } else if (this.chartType === 'knitwear') {
      body = this.sectionHtml('knitwear', 'Knitwear', 'Knitwear measurements', this.chartData('knitwear'), 'knitwear');
    } else {
      if (this.showJacket) {
        body += this.sectionHtml(
          'jacket',
          this.sectionTitle('jacket'),
          'Jacket measurements',
          this.chartData('jacket'),
          'jacket'
        );
      }
      if (this.showTrousers) {
        body += this.sectionHtml(
          'trouser',
          this.sectionTitle('trouser'),
          'Trousers measurements',
          this.chartData('trouser'),
          'trouser'
        );
      }
    }

    this.root.innerHTML = `
      ${lengthTabs}
      ${body}
      ${this.showConversions && !isSimple ? this.conversionsHtml() : ''}
    `;
    this.root.querySelector(`[data-fit="${this.fit}"]`)?.classList.add('active');
    this.applyUnit();
  }

  measureCategory(type) {
    if (type === 'trouser') return 'trouser';
    return 'jacket';
  }

  measureKey(label) {
    if (typeof SS_MEASURE_LABEL_KEYS !== 'undefined' && SS_MEASURE_LABEL_KEYS[label]) {
      return SS_MEASURE_LABEL_KEYS[label];
    }
    return label.toLowerCase().replace(/\s+/g, '-');
  }

  sectionHtml(type, title, subtitle, data, sizeKey) {
    const category = this.measureCategory(type);
    const sizes = this.chartSizes(sizeKey);
    const headerCells = sizes
      .map(
        (s) => `
        <th class="ss-chart__size-col">
          <span class="ss-chart__size-num">${s.num}</span>
          ${s.alpha ? `<span class="ss-chart__size-alpha">${s.alpha}</span>` : ''}
        </th>`
      )
      .join('');

    const labelRows = data.rows
      .map(
        (row, i) => `
        <tr class="${i % 2 === 0 ? 'ss-chart__row--alt' : ''}">
          <th class="ss-chart__row-label">
            <button
              type="button"
              class="ss-chart__info-icon"
              data-measure-guide="${this.measureKey(row.label)}"
              data-measure-category="${category}"
              aria-label="How to measure ${row.label.toLowerCase()}"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="6.5" stroke="currentColor"/><path d="M7 6.2v3.6M7 4.8h.01" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
            </button>
            ${row.label}
          </th>
        </tr>`
      )
      .join('');

    const valueRows = data.rows
      .map(
        (row, i) => `
        <tr class="${i % 2 === 0 ? 'ss-chart__row--alt' : ''}">
          ${row.values
            .map((v) => `<td class="ss-chart__value" data-measure-cm="${v}">${this.formatValue(v)}</td>`)
            .join('')}
        </tr>`
      )
      .join('');

    return `
      <section class="ss-chart__section" data-chart-section="${type}">
        <div class="ss-chart__section-head">
          <div class="ss-chart__section-titles">
            <h3 class="ss-chart__section-title">${title}</h3>
            <p class="ss-chart__section-sub">${subtitle}</p>
          </div>
          <div class="ss-chart__scroll-nav">
            <button type="button" class="ss-chart__scroll-btn" data-scroll="prev" data-scroll-target="${type}" aria-label="Scroll left">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <button type="button" class="ss-chart__scroll-btn" data-scroll="next" data-scroll-target="${type}" aria-label="Scroll right">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </div>
        <div class="ss-chart__table-wrap">
          <div class="ss-chart__labels">
            <table class="ss-chart__table ss-chart__table--labels">
              <thead>
                <tr>
                  <th class="ss-chart__corner">
                    <div class="ss-chart__unit-switch" role="group" aria-label="Measurement unit">
                      <button type="button" class="ss-chart__unit-btn" data-unit="cm" aria-pressed="true">cm</button>
                      <button type="button" class="ss-chart__unit-btn" data-unit="inch" aria-pressed="false">inch</button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>${labelRows}</tbody>
            </table>
          </div>
          <div class="ss-chart__scroll" data-scroll-area="${type}">
            <table class="ss-chart__table ss-chart__table--values">
              <thead>
                <tr>${headerCells}</tr>
              </thead>
              <tbody>${valueRows}</tbody>
            </table>
          </div>
        </div>
      </section>`;
  }

  conversionsHtml() {
    const cards = SS_SIZE_CHART_META.conversions
      .map(
        (c) => `
        <article class="ss-chart__conv-card">
          <header class="ss-chart__conv-head">
            <span class="ss-chart__conv-size">${c.eu}</span>
            <span class="ss-chart__conv-alpha">${c.alpha}</span>
          </header>
          <dl class="ss-chart__conv-list">
            <div><dt>International</dt><dd>${c.alpha}</dd></div>
            <div><dt>EU standard</dt><dd>${c.eu}</dd></div>
            <div><dt>UK/US jacket</dt><dd>${c.usJacket}</dd></div>
            <div><dt>UK/US trouser</dt><dd>${c.usTrouser}</dd></div>
            <div><dt>China jacket</dt><dd>${c.chinaJacket}</dd></div>
            <div><dt>China trouser</dt><dd>${c.chinaTrouser}</dd></div>
          </dl>
        </article>`
      )
      .join('');

    return `
      <section class="ss-chart__section ss-chart__section--conversions">
        <div class="ss-chart__section-head">
          <h3 class="ss-chart__section-title">Size conversions</h3>
          <div class="ss-chart__scroll-nav">
            <button type="button" class="ss-chart__scroll-btn" data-scroll="prev" data-scroll-target="conversions" aria-label="Scroll left">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <button type="button" class="ss-chart__scroll-btn" data-scroll="next" data-scroll-target="conversions" aria-label="Scroll right">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </div>
        <div class="ss-chart__scroll ss-chart__scroll--cards" data-scroll-area="conversions">${cards}</div>
      </section>`;
  }

  formatValue(cm) {
    if (this.unit === 'cm') {
      return Number.isInteger(cm) ? String(cm) : cm.toFixed(1);
    }
    const inch = cm / 2.54;
    return inch % 1 === 0 ? String(Math.round(inch)) : inch.toFixed(1);
  }

  applyUnit() {
    this.root.querySelectorAll('.ss-chart__unit-btn').forEach((btn) => {
      const active = btn.dataset.unit === this.unit;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    this.root.querySelectorAll('[data-measure-cm]').forEach((cell) => {
      const cm = parseFloat(cell.dataset.measureCm);
      cell.textContent = this.formatValue(cm);
    });
  }

  bind() {
    this.root.addEventListener('click', (e) => {
      const fitTab = e.target.closest('[data-fit]');
      if (fitTab) {
        this.fit = fitTab.dataset.fit;
        this.render();
        return;
      }

      const unitBtn = e.target.closest('[data-unit]');
      if (unitBtn) {
        this.unit = unitBtn.dataset.unit;
        this.applyUnit();
        return;
      }

      const scrollBtn = e.target.closest('[data-scroll]');
      if (scrollBtn) {
        const target = scrollBtn.dataset.scrollTarget;
        const area = this.root.querySelector(`[data-scroll-area="${target}"]`);
        if (!area) return;
        const delta = scrollBtn.dataset.scroll === 'next' ? 200 : -200;
        area.scrollBy({ left: delta, behavior: 'smooth' });
        return;
      }

      const guideBtn = e.target.closest('[data-measure-guide]');
      if (guideBtn && typeof window.openSsMeasureGuide === 'function') {
        e.preventDefault();
        window.openSsMeasureGuide({
          category: guideBtn.dataset.measureCategory,
          measureKey: guideBtn.dataset.measureGuide,
        });
      }
    });
  }
}

function initSsSizeCharts(scope = document) {
  if (typeof SS_SIZE_CHART_FITS === 'undefined') return;
  scope.querySelectorAll('[data-ss-chart]').forEach((el) => {
    el._ssChart = new SsSizeChart(el);
  });
}

if (typeof window !== 'undefined') {
  window.initSsSizeCharts = initSsSizeCharts;
}
